import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowRight, Check, Loader2, Sparkles, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { track, getAttribution } from '../lib/analytics';
import { EXIT_INTENT_STORAGE_KEY, suppressExitIntent } from '../lib/exitIntent';

/**
 * Exit-intent capture modal.
 *
 * Triggers:
 *   - Desktop only: cursor leaves the viewport from the top, AND the user
 *     isn't actively engaged (no focused input, Calendly iframe not on
 *     screen, no clicks/typing/scrolls in the last 3 seconds).
 *   - Mobile: no auto-trigger. Mobile lacks a reliable exit-intent signal
 *     and time-based timers interrupt users mid-form (especially the
 *     Calendly booking flow). Mobile visitors still convert via the on-page
 *     CTAs and the modal will not pop up unattended.
 *
 * Frequency cap:
 *   - Once per browser session (sessionStorage). Conversions (Calendly
 *     booking, estimate form submit, savings widget submit) all lock the
 *     cooldown via `suppressExitIntent()` from `lib/exitIntent.js`.
 */

const STORAGE_KEY = EXIT_INTENT_STORAGE_KEY;
const READY_DELAY_MS = 4_000; // ignore mouseouts in the first 4s of page load
const RECENT_ACTIVITY_MS = 3_000; // suppress if user interacted in last 3s

const ORDER_BUCKETS = [
  '<500',
  '500–2,000',
  '2,000–10,000',
  '10,000–50,000',
  '50,000+',
];

// Midpoint of each bucket — used to ballpark savings for the success-state
// "look how much you'd save" line. Keep these conservative (not the high end)
// so we don't oversell vs. the actual quote we email back.
const BUCKET_MIDPOINTS = {
  '<500': 250,
  '500–2,000': 1_250,
  '2,000–10,000': 6_000,
  '10,000–50,000': 30_000,
  '50,000+': 60_000,
};

/**
 * Same model as /components/Calculator.jsx but with safe defaults so we can
 * estimate from the modal's three fields alone:
 *   - avgItems = 1.5 (typical DTC)
 *   - avgShipping = $8 / order
 *   - pallets = max(1, round(orders / 500))  (rough storage proxy)
 * Returns the estimated monthly savings vs. a traditional 3PL, rounded down
 * to the nearest $50 so the number always feels grounded, not pumped.
 */
function estimateMonthlySavings(bucket) {
  const orders = BUCKET_MIDPOINTS[bucket];
  if (!orders) return 0;
  const avgItems = 1.5;
  const avgShipping = 8;
  const pallets = Math.max(1, Math.round(orders / 500));

  const tradPick = orders * (3 + (avgItems - 1) * 0.25);
  const smPick = orders * 2;
  const tradStorage = pallets * 25;
  const smStorage = pallets * 25 * 0.6;
  const tradShipping = orders * avgShipping;
  const smShipping = orders * avgShipping * 0.75;

  const monthly = (tradPick + tradStorage + tradShipping) - (smPick + smStorage + smShipping);
  return Math.max(0, Math.floor(monthly / 50) * 50);
}

const fmtUSD = (n) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function isMobile() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 768px)').matches;
}

function withinCooldown() {
  try {
    // sessionStorage clears when the browser tab/window closes, so each
    // fresh session is automatically eligible for the modal again.
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function lockCooldown() {
  suppressExitIntent();
}

export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [monthlyOrders, setMonthlyOrders] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const readyAtRef = useRef(0);
  const armedRef = useRef(true); // false once we've shown the modal this session

  const reveal = useCallback(() => {
    if (!armedRef.current) return;
    if (withinCooldown()) {
      armedRef.current = false;
      return;
    }
    armedRef.current = false;
    setOpen(true);
    track('exit_intent_modal_shown', {
      trigger: 'desktop_mouseleave',
    });
  }, []);

  // Arm the trigger once. Tracks page-ready timestamp + last user-activity
  // timestamp so we can ignore initial mouseleaves and not interrupt
  // someone who's actively engaged.
  useEffect(() => {
    readyAtRef.current = Date.now() + READY_DELAY_MS;
    if (withinCooldown()) {
      armedRef.current = false;
      return undefined;
    }
    if (isMobile()) {
      // Mobile: no auto-trigger. See top-of-file comment for the reasoning.
      armedRef.current = false;
      return undefined;
    }

    // Track recent activity so we can suppress the modal if the user is
    // mid-action when the cursor briefly grazes the top of the viewport.
    let lastActivityAt = 0;
    const markActive = () => { lastActivityAt = Date.now(); };
    window.addEventListener('keydown', markActive);
    window.addEventListener('click', markActive);
    window.addEventListener('scroll', markActive, { passive: true });
    window.addEventListener('touchstart', markActive, { passive: true });

    const isInputFocused = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        el.isContentEditable === true
      );
    };

    // Calendly is embedded as an iframe — if any iframe is currently
    // visible in the viewport, the user is most likely interacting with
    // it (and the cursor "exiting" the page is usually just it crossing
    // into the iframe). Don't fire then.
    const isIframeVisible = () => {
      const iframes = document.getElementsByTagName('iframe');
      const vh = window.innerHeight || 0;
      for (let i = 0; i < iframes.length; i += 1) {
        const rect = iframes[i].getBoundingClientRect();
        if (rect.height === 0 || rect.width === 0) continue;
        // Skip tracking pixels / hidden tags (1x1 iframes from analytics
        // vendors). These are technically "visible" but never intercept a
        // real cursor — counting them would suppress exit-intent forever.
        if (rect.height < 5 || rect.width < 5) continue;
        // Any vertical overlap with the viewport counts as "visible".
        if (rect.bottom > 0 && rect.top < vh) return true;
      }
      return false;
    };

    // Desktop trigger: `mouseleave` on documentElement fires exactly once
    // when the cursor exits the viewport. Gate on `clientY` to filter to
    // top-edge exits, plus the engagement checks above.
    const handleLeave = (e) => {
      if (!armedRef.current) return;
      if (Date.now() < readyAtRef.current) return;
      if (e.clientY > 10) return; // not a top-edge exit
      if (isInputFocused()) return; // mid-form
      if (isIframeVisible()) return; // Calendly / other embed in view
      if (Date.now() - lastActivityAt < RECENT_ACTIVITY_MS) return; // active
      reveal();
    };

    document.documentElement.addEventListener('mouseleave', handleLeave);

    return () => {
      document.documentElement.removeEventListener('mouseleave', handleLeave);
      window.removeEventListener('keydown', markActive);
      window.removeEventListener('click', markActive);
      window.removeEventListener('scroll', markActive);
      window.removeEventListener('touchstart', markActive);
    };
  }, [reveal]);

  const handleClose = () => {
    setOpen(false);
    if (!submitted) {
      lockCooldown();
      track('exit_intent_modal_dismiss', {});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedSite = website.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!trimmedSite) {
      toast.error('Please add your website');
      return;
    }
    if (!monthlyOrders) {
      toast.error('Please pick your monthly order volume');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/leads/exit-intent`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: trimmedEmail,
            phone: phone.trim(),
            website: trimmedSite,
            monthlyOrders,
            source_path: typeof window !== 'undefined' ? window.location.pathname : '',
            attribution: getAttribution(),
          }),
        },
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || 'Failed to submit');
      }
      track('exit_intent_modal_submit', {
        email_domain: trimmedEmail.split('@')[1] || '',
        monthly_orders: monthlyOrders,
        estimated_monthly_savings: estimateMonthlySavings(monthlyOrders),
        // Soft lead — bidding-platform value sits below form_estimate_submit
        // since these prospects were already on their way out.
        value: 50,
        currency: 'USD',
      });
      setSubmitted(true);
      lockCooldown();
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={handleClose}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        data-testid="exit-intent-backdrop"
      />

      {/* Card */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl ring-1 ring-slate-200 overflow-hidden animate-fade-up">
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          data-testid="exit-intent-close-btn"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-white text-slate-500 hover:text-slate-900 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Gradient header band */}
        <div className="brand-gradient px-7 sm:px-9 py-7 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-xs font-semibold uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5" />
            Custom Quote
          </div>
          <h2
            id="exit-intent-title"
            className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight"
          >
            Hold up. Let us beat your current 3PL rate, guaranteed.
          </h2>
          <p className="mt-2 text-indigo-50 text-sm sm:text-base">
            Drop your details below. We&apos;ll send a side-by-side breakdown showing what you&apos;d save with Shipmaster — within 24 hours, no sales calls.
          </p>
        </div>

        {/* Body */}
        <div className="px-7 sm:px-9 py-7">
          {submitted ? (
            (() => {
              const monthlySavings = estimateMonthlySavings(monthlyOrders);
              return (
                <div className="text-center py-2" data-testid="exit-intent-success">
                  <div className="w-14 h-14 mx-auto rounded-full bg-indigo-600 flex items-center justify-center mb-3">
                    <Check className="w-7 h-7 text-white" strokeWidth={3} />
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900">You&apos;re on the list.</h3>

                  {monthlySavings > 0 && (
                    <div
                      className="mt-4 mb-1 mx-auto max-w-sm rounded-2xl border border-indigo-100 bg-indigo-50/70 px-4 py-3.5"
                      data-testid="exit-intent-savings-estimate"
                    >
                      <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
                        Ballpark estimate
                      </p>
                      <p className="mt-1 text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">
                        ~{fmtUSD(monthlySavings)}<span className="text-base font-bold text-slate-500">/mo</span>
                      </p>
                      <p className="mt-1 text-sm text-slate-600">
                        That&apos;s ~{fmtUSD(monthlySavings * 12)}/year at your volume vs. a typical 3PL.
                      </p>
                    </div>
                  )}

                  <p className="mt-3 text-slate-600">
                    Full breakdown headed to{' '}
                    <span data-clarity-mask="true" className="font-semibold text-slate-900">{email.trim()}</span> within 24 hours.
                  </p>
                  <Button
                    onClick={handleClose}
                    className="mt-5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-6"
                    data-testid="exit-intent-success-close-btn"
                  >
                    Got it
                  </Button>
                  <p className="mt-3 text-[11px] text-slate-400">
                    Estimate based on industry averages. Your actual quote may differ.
                  </p>
                </div>
              );
            })()
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="exit-intent-form">
              <div>
                <label htmlFor="exit-intent-email" className="block text-sm font-semibold text-slate-800 mb-1.5">
                  Work email
                </label>
                <Input
                  id="exit-intent-email"
                  type="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbrand.com"
                  className="h-12 rounded-xl text-base"
                  data-testid="exit-intent-email-input"
                />
              </div>
              <div>
                <label htmlFor="exit-intent-website" className="block text-sm font-semibold text-slate-800 mb-1.5">
                  Website
                </label>
                <Input
                  id="exit-intent-website"
                  type="text"
                  required
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="yourbrand.com"
                  className="h-12 rounded-xl text-base"
                  data-testid="exit-intent-website-input"
                />
              </div>
              <div>
                <label htmlFor="exit-intent-phone" className="block text-sm font-semibold text-slate-800 mb-1.5">
                  Phone <span className="ml-1 text-xs font-medium text-slate-400">(optional)</span>
                </label>
                <Input
                  id="exit-intent-phone"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="h-12 rounded-xl text-base"
                  data-testid="exit-intent-phone-input"
                />
              </div>
              <div>
                <label htmlFor="exit-intent-orders" className="block text-sm font-semibold text-slate-800 mb-1.5">
                  Monthly orders
                </label>
                <select
                  id="exit-intent-orders"
                  required
                  value={monthlyOrders}
                  onChange={(e) => setMonthlyOrders(e.target.value)}
                  data-testid="exit-intent-orders-select"
                  className="w-full h-12 rounded-xl text-base bg-white border border-slate-200 px-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="" disabled>
                    Select a range…
                  </option>
                  {ORDER_BUCKETS.map((b) => (
                    <option key={b} value={b}>
                      {b} orders / month
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                data-testid="exit-intent-submit-btn"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    Show Me My Savings
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
              <p className="text-center text-xs text-slate-500">
                No spam. We reply once with a quote, then it&apos;s up to you.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
